import React from "react";
import {
  Home,
  Building2,
  Sofa,
  Package,
  Wrench,
  Trash2,
  Trees,
  HardDrive,
  Warehouse,
  ShieldAlert,
  ArrowRight,
  CheckCircle,
  Truck
} from "lucide-react";
import { Button } from "./ui/button";
import { useQuoteModal } from "@/hooks/useQuoteModal";

interface ServiceItem {
  icon: React.ElementType;
  title: string;
  description: string;
  keywords: string[];
}

const services: ServiceItem[] = [
  {
    icon: Home,
    title: "Residential Junk Removal",
    description: "Full-service house clutter, furniture, mattress, and trash pickup for Davie homeowners and renters.",
    keywords: ["Davie Home Junk Removal", "Clutter Pickup 33314", "House Waste Hauling"]
  },
  {
    icon: Building2,
    title: "Commercial Junk Hauling",
    description: "Office furniture, retail debris, warehouse clearing, and commercial trash removal across Davie & Broward.",
    keywords: ["Office Cleanout Davie", "Commercial Hauling FL", "Retail Waste Clearance"]
  },
  {
    icon: Sofa,
    title: "Furniture Removal & Disposal",
    description: "Couches, sectional sofas, recliners, mattresses, heavy dining tables — hauled out without scratching your walls.",
    keywords: ["Couch Pickup Davie", "Mattress Removal FL", "Heavy Furniture Hauling"]
  },
  {
    icon: Package,
    title: "Appliance Pickup & Recycling",
    description: "Refrigerators, washing machines, dryers, dishwashers, and water heaters safely disconnected and hauled away.",
    keywords: ["Fridge Removal Davie", "Washer Dryer Disposal", "E-Appliance Recycling"]
  },
  {
    icon: Wrench,
    title: "Garage, Attic & Basement Cleanouts",
    description: "Reclaim your parking space or storage rooms. We do all the heavy lifting, sorting, loading, and sweeping.",
    keywords: ["Garage Cleanout Davie", "Attic Debris Removal", "Basement Storage Clearing"]
  },
  {
    icon: Warehouse,
    title: "Storage Unit Cleanouts",
    description: "Complete storage facility clearing. Avoid monthly fees by emptying your Davie storage unit top to bottom.",
    keywords: ["Davie Storage Cleanout", "Storage Locker Hauling", "Unit Trash Removal"]
  },
  {
    icon: Trash2,
    title: "Construction Debris Removal",
    description: "Post-renovation drywall, wood, tile, roofing, concrete, and remodeling waste hauled away fast.",
    keywords: ["Davie Construction Hauling", "Renovation Waste Pickup", "Tile Debris Haul"]
  },
  {
    icon: Trees,
    title: "Yard Waste & Landscape Debris",
    description: "Branches, palm fronds, storm cleanup, tree limbs, fencing, and outdoor junk removed clean.",
    keywords: ["Davie Yard Waste Removal", "Storm Debris Cleanup", "Palm Frond Hauling"]
  },
  {
    icon: HardDrive,
    title: "Electronics & E-Waste Recycling",
    description: "Eco-friendly disposal and recycling of old TVs, computers, monitors, printers, and outdated tech.",
    keywords: ["TV Disposal Davie", "E-Waste Recycling FL", "Computer Hauling"]
  },
  {
    icon: ShieldAlert,
    title: "Estate & Foreclosure Cleanouts",
    description: "Respectful, fast property cleanout services for realtors, probate lawyers, property managers, and families.",
    keywords: ["Estate Cleanout Davie FL", "Foreclosure Hauling", "REO Property Cleanup"]
  },
  {
    icon: Building2,
    title: "Eviction Property Cleanouts",
    description: "Rapid-response property clearing for landlords and real estate investors in Davie and Broward County.",
    keywords: ["Eviction Cleanout Davie", "Landlord Trash Removal", "Property Clearing FL"]
  },
  {
    icon: Home,
    title: "Compassionate Hoarding Cleanout",
    description: "Judgment-free, discreet, respectful heavy cleanout and decluttering assistance tailored to your needs.",
    keywords: ["Hoarding Cleanup Davie", "Discreet Junk Hauling", "Heavy Decluttering FL"]
  }
];

export const ServicesSection: React.FC = () => {
  const { openModal } = useQuoteModal();
  return (
    <section id="services" className="py-20 bg-slate-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-400/20 border border-amber-400/40 text-slate-900 font-extrabold px-4 py-1.5 rounded-full text-xs uppercase tracking-widest mb-4">
            <Truck className="w-4 h-4 text-primary" /> Full-Service Local Junk Haulers
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black font-heading text-slate-950 uppercase tracking-tight mb-4">
            Junk Removal Services <span className="text-primary">Davie</span> & Broward County Trusts
          </h2>
          <p className="text-lg text-slate-600 font-medium">
            From a single couch or heavy refrigerator to an entire property cleanout — our full-service junk removal covers Davie, Fort Lauderdale, Hollywood, Plantation, Cooper City, Sunrise & all of South Florida. If it's not radioactive, our muscle crew will haul it away today!
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={index}
                className="group bg-white border-2 border-slate-200 hover:border-amber-400 rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                      <Icon className="w-7 h-7 text-primary group-hover:text-white transition-colors" />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 group-hover:text-amber-500">
                      Davie FL Service
                    </span>
                  </div>

                  <h3 className="text-xl font-black text-slate-950 font-heading mb-2 group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>

                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                </div>

                <div>
                  {/* Local SEO keywords tags */}
                  <div className="flex flex-wrap gap-1.5 pt-3 border-t border-slate-100 mb-4">
                    {service.keywords.map((kw, kIndex) => (
                      <span
                        key={kIndex}
                        className="text-[10px] font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded"
                      >
                        #{kw}
                      </span>
                    ))}
                  </div>

                  <button
                    onClick={openModal}
                    className="inline-flex items-center text-xs font-black uppercase tracking-wider text-primary group-hover:text-rose-700 transition-colors"
                  >
                    <span>Get Quote For This Service</span>
                    <ArrowRight className="w-3.5 h-3.5 ml-1 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Callout box under Services */}
        <div className="mt-14 bg-slate-950 text-white rounded-3xl p-8 sm:p-10 border-2 border-amber-400/40 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-80 h-80 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
          
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="max-w-2xl">
              <span className="text-amber-400 text-xs font-black uppercase tracking-widest">
                Don't See Your Junk Listed?
              </span>
              <h3 className="text-2xl sm:text-3xl font-black font-heading mt-1 mb-2">
                If It's Non-Hazardous &amp; Needs Heavy Lifting, We'll Haul It!
              </h3>
              <p className="text-slate-300 text-sm sm:text-base">
                Hot tubs, sheds, playsets, heavy machinery, piano removal, or bulk yard clearing in Davie FL. Call us right now for an instant quote.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
              <a href="tel:9542808090" className="w-full sm:w-auto">
                <Button size="lg" className="w-full bg-amber-400 text-slate-950 hover:bg-amber-300 font-extrabold uppercase">
                  Call (954) 280-8090
                </Button>
              </a>
              <Button size="lg" onClick={openModal} className="w-full !bg-rose-600 hover:!bg-rose-700 text-white font-extrabold uppercase shadow-lg shadow-rose-500/30 border-0">
                  Online Free Quote
                </Button>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
